import { useState } from "react"
import {
	AudioPlayer,
	Badge,
	Box,
	Icons,
	Inline,
	joinAttributes,
	RelativeTime,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
	Time,
	TimeDuration,
} from "@sembark-travel/ui-base"
import { ListView, TSearchParams } from "@sembark-travel/ui-list"
import { ITripPlanRequest } from "./store"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { Link } from "@sembark-travel/ui-router"
import { Destinations } from "../Trips"
import {
	duration,
	isAfter,
	utcTimestampToLocalDateTimeString,
} from "@sembark-travel/datetime-utils"
import { TCallLog } from "../CallLogs"
import { MailMessagesList } from "../MailMessages"

type TPastInteractionsFilters = TSearchParams & {
	type: "calls" | "mails"
}

export function PastInteractionsInDialog({
	hasPastInteractions,
	children,
	requestId,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	requestId: ITripPlanRequest["id"]
	hasPastInteractions: ITripPlanRequest["has_past_interactions"]
}) {
	const [isOpenDialog, openDialog, closeDialog] = useDialog()
	const [params, setParams] = useState<TPastInteractionsFilters>({
		type: hasPastInteractions?.calls ? "calls" : "mails",
	})
	return (
		<>
			{children({
				open: openDialog,
			})}
			<Dialog
				open={isOpenDialog}
				onClose={closeDialog}
				title="Past Interactions"
				lg
			>
				<Tabs layout="col">
					<TabsList>
						{hasPastInteractions?.calls ? (
							<TabItem
								onClick={() => {
									setParams({
										...params,
										type: "calls",
									})
								}}
								active={params.type === "calls"}
							>
								Calls
							</TabItem>
						) : null}
						{hasPastInteractions?.mails ? (
							<TabItem
								onClick={() => {
									setParams({
										...params,
										type: "mails",
									})
								}}
								active={params.type === "mails"}
							>
								Mails
							</TabItem>
						) : null}
					</TabsList>
					<TabContent>
						{params.type === "mails" ? (
							<MailMessagesList
								modelType="trip_plan_requests"
								modelId={requestId}
								title={null}
								emptyStateMessage="No email messages are linked to this lead yet."
							/>
						) : (
							<CallsList
								requestId={requestId}
								params={params}
								setParams={setParams}
							/>
						)}
					</TabContent>
				</Tabs>
			</Dialog>
		</>
	)
}

function CallsList({
	requestId,
	params,
	setParams,
}: {
	requestId: number
	params: TPastInteractionsFilters
	setParams: React.Dispatch<React.SetStateAction<TPastInteractionsFilters>>
}) {
	return (
		<ListView<TCallLog, TPastInteractionsFilters>
			pageKey={`past-interactions-${requestId}-${params.type}`}
			params={params}
			onPageChange={(page) => setParams({ ...params, page })}
			fetch={async (xhr, { type, page, limit }) => {
				const res = await xhr.get(
					`/trip-plan-requests/${requestId}/past-interactions`,
					{
						params: {
							type: type,
							page: page,
							limit: limit ?? 10,
						},
					}
				)
				return res.data
			}}
		>
			{({ items }) => (
				<Stack borderWidth="1" rounded="md">
					{items.map((log) => (
						<Inline
							key={log.id}
							borderBottomWidth={
								log.id !== items[items.length - 1].id && !log.is_self
									? "1"
									: undefined
							}
							alignItems={"center"}
							borderColor={log.is_self ? "accent_emphasis" : undefined}
							justifyContent="between"
							padding="3"
							borderWidth={log.is_self ? "2" : undefined}
							bgColor={log.is_self ? "accent" : "default"}
							flexWrap={"wrap"}
							gap={"2"}
						>
							<Inline flex={"1"} gap="2" alignItems="center">
								<Box
									bgColor={
										log.call_direction === "IBD"
											? log.is_missed_call
												? "danger"
												: "inset"
											: log.is_missed_call
												? "warning"
												: "success"
									}
									rounded="md"
									paddingX="2"
									paddingY={"1"}
								>
									{log.is_missed_call ? (
										log.call_direction === "IBD" ? (
											<Icons.MissedCall color="danger" />
										) : (
											<Icons.OutboundMissedCall size={"5"} color={"warning"} />
										)
									) : log.call_direction === "IBD" ? (
										<Icons.IncomingCall color="muted" />
									) : (
										<Icons.OutgoingCall color="success" />
									)}
								</Box>

								<Stack gap="px">
									{log.agent ? (
										<>
											<Text color={"muted"} fontSize="sm">
												Agent
											</Text>
											<Inline gap="1" alignItems={"center"}>
												<Text fontWeight="semibold">{log.agent.name}</Text>
												<Text fontSize={"sm"} color={"muted"}>
													{log.agent.normalized_phone}
												</Text>
												{log.agent.deleted_at ? (
													<Icons.Trash
														title={utcTimestampToLocalDateTimeString(
															log.agent.deleted_at
														)}
														color="danger"
													/>
												) : null}
											</Inline>
										</>
									) : (
										<Box>
											<i>
												<Text
													color={
														log.call_direction === "OBD" ? "warning" : "danger"
													}
												>
													{log.call_direction === "OBD"
														? "Outbound - Missed Call"
														: "Missed Call"}
												</Text>
											</i>
										</Box>
									)}
									<Text fontSize="sm" color="muted">
										<RelativeTime timestamp={log.created_at} />
									</Text>
								</Stack>
							</Inline>

							<Inline
								flex={"1"}
								justifyContent={"center"}
								alignItems={"center"}
							>
								{log.call_recording_url && !log.is_missed_call ? (
									<AudioPlayer src={log.call_recording_url} />
								) : null}
							</Inline>

							<Inline
								flex={"1"}
								gap="2"
								justifyContent={"end"}
								alignItems="center"
							>
								{log.trip ? (
									<Stack gap="1" alignItems={"end"} fontSize={"sm"}>
										<Link to={`/trips/${log.trip.id}`} anchored>
											<Badge status="success" fullRounded>
												Trip #{log.trip.id}
											</Badge>
										</Link>
										<Box fontWeight="semibold">
											<Destinations destinations={log.trip.destinations} />
										</Box>
										<Box color="muted">
											{joinAttributes(
												<Time value={log.trip.start_date} format="DD MMM" />,
												<TimeDuration value={duration(log.trip.days, "days")}>
													{log.trip.nights}N
												</TimeDuration>,
												<Text as="span">
													{log.trip.no_of_adults}A
													{log.trip.no_of_children
														? `, ${log.trip.no_of_children}C`
														: null}
												</Text>,
												isAfter(new Date(), log.trip.end_date) ? (
													<Text as="span" color="danger">
														<Icons.Info title="Past Dates" />
													</Text>
												) : null
											)}
										</Box>
									</Stack>
								) : log.trip_plan_request ? (
									<Badge status="warning" fullRounded>
										TPR #{log.trip_plan_request.id}
									</Badge>
								) : null}
							</Inline>
						</Inline>
					))}
				</Stack>
			)}
		</ListView>
	)
}
